/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { useTypedSelector } from 'helpers/reducers'
import { useMemo } from 'react'
import { HallOpeningHourEntries } from 'types/entities/HallOpeningHourEntries'
import { Dayjs } from 'dayjs'
import { PackageCategory } from 'types/enums/PackageCategory'

export const useHallOpeningHours = () => {
    const hall = useTypedSelector((state) => state.hallState.hall)
    const bookingFilter = useTypedSelector(
        (state) => state.bookingState.bookingProcess.filter
    )

    // Memoize the excluded days calculation
    const excludedDays = useMemo(() => {
        return (
            hall?.excludedOpeningDays.map((excludedOpeningDay) => {
                return new Date(excludedOpeningDay)
            }) || []
        )
    }, [hall?.excludedOpeningDays])

    const getExcludedDays = (): Date[] => {
        return excludedDays
    }

    const getOpeningHoursForDate = (
        date: Date
    ): HallOpeningHourEntries | null => {
        if (!hall || !hall.hallOpeningHoursEntries) {
            return null
        }

        const dateEntry = date.toISOString().split('T')[0]
        const hallOIndex = hall.hallOpeningHoursEntries.findIndex(
            (hallOpeningHour) => hallOpeningHour.date === dateEntry
        )
        return hall.hallOpeningHoursEntries[hallOIndex]
    }

    const timeslotOutsideOpeningHours = (date: Date): boolean => {
        const hallOpeningHourEntry = getOpeningHoursForDate(date)
        if (!hallOpeningHourEntry) {
            return true
        }

        return (
            date.getHours() < hallOpeningHourEntry.from_hours ||
            date.getHours() >= hallOpeningHourEntry.to_hours
        )
    }

    // check if package with selected timeslot has ended after final hours of hall
    const packageHasEndedAfterHallOpeningHours = (
        dateForCompare: Dayjs | null,
        packageDurationHours: number = 4
    ): boolean => {
        // check if have date for compare
        if (!dateForCompare) {
            return true
        }

        // check if have opening hours
        const openingHours = getOpeningHoursForDate(dateForCompare.toDate())
        if (!openingHours) {
            return true
        }

        // end time of package with duration
        const packageEndDate = dateForCompare.add(packageDurationHours, 'hour')

        // check if the same day
        if (
            !dateForCompare.startOf('day').isSame(packageEndDate.startOf('day'))
        ) {
            return true
        }

        const packageEndTime =
            packageEndDate.hour() * 60 + packageEndDate.minute()
        const closingTime = openingHours.to_hours * 60 + openingHours.to_minutes

        // check if sum of minutes package end time is largest than opening time
        return packageEndTime > closingTime
    }

    const packageCategoryPreSummer = () => {
        if (bookingFilter.category === PackageCategory.School) {
            const isPreSummerWeekday = (): boolean => {
                const startDate = new Date('2025-06-16')
                const endDate = new Date('2025-07-04')
                const bookingDate = new Date(bookingFilter.date)

                // Check if date is within range and is a weekday (Monday to Friday)
                if (
                    bookingDate.getTime() >= startDate.getTime() &&
                    bookingDate.getTime() <= endDate.getTime() &&
                    bookingDate.getDay() >= 1 &&
                    bookingDate.getDay() <= 5 // Monday is 1, Friday is 5
                ) {
                    return true
                }
                return false
            }

            return isPreSummerWeekday()
        }
        return false
    }
    return {
        packageCategoryPreSummer,
        getExcludedDays,
        getOpeningHoursForDate,
        timeslotOutsideOpeningHours,
        packageHasEndedAfterHallOpeningHours,
    }
}
